import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { UserCompanyMemberDto } from '@monorepo/shared';
import { UsersTableActionMenu } from './UsersTableActionMenu';
import { UsersTableRow } from './UsersTableRow';
import { BulkActionsBar, type BulkAction } from './BulkActionsBar';

interface UsersTableProps {
  users: UserCompanyMemberDto[];
  onUserAction: (userId: string, action: 'edit' | 'remove' | 'resend') => void;
  isCurrentUserAdmin: boolean;
  removeActionLabel?: string;
  onBulkResend?: (userIds: string[]) => void;
  onBulkRemove?: (userIds: string[]) => void;
}

export const UsersTable = ({
  users,
  onUserAction,
  isCurrentUserAdmin,
  removeActionLabel,
  onBulkResend,
  onBulkRemove,
}: UsersTableProps) => {
  const { t } = useTranslation();
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  const removeLabel = removeActionLabel ?? t('admin.users.actions.remove_from_company');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuId) {
        const target = event.target as HTMLElement;
        if (!target.closest('.action-menu') && !target.closest('.action-button')) {
          setOpenMenuId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const handleMenuToggle = (userId: string) => {
    if (openMenuId === userId) {
      setOpenMenuId(null);
    } else {
      const button = buttonRefs.current[userId];
      if (button) {
        const rect = button.getBoundingClientRect();
        setMenuPosition({
          top: rect.bottom + 8,
          right: window.innerWidth - rect.right,
        });
      }
      setOpenMenuId(userId);
    }
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === users.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.map((u) => u.id)));
    }
  };

  const toggleSelectUser = (userId: string) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const runBulkAction = (action: (userIds: string[]) => void) => {
    action(Array.from(selectedUsers));
    setSelectedUsers(new Set());
  };

  const bulkActions: BulkAction[] = [];
  if (onBulkResend) {
    bulkActions.push({
      key: 'resend',
      label: t('admin.users.actions.resend_invite'),
      onClick: () => runBulkAction(onBulkResend),
    });
  }
  if (onBulkRemove) {
    bulkActions.push({
      key: 'remove',
      label: removeLabel,
      onClick: () => runBulkAction(onBulkRemove),
      variant: 'danger',
    });
  }

  return (
    <div className="flex flex-col w-full h-full overflow-hidden">
      {selectedUsers.size > 0 && (
        <BulkActionsBar count={selectedUsers.size} actions={bulkActions} />
      )}

      <div className="overflow-auto ">
        <table className="w-full border-separate border-spacing-y-3">
          <thead className="bg-[#f4f5f6] sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2.5 text-left rounded-l-md">
                <input
                  type="checkbox"
                  checked={selectedUsers.size === users.length && users.length > 0}
                  onChange={toggleSelectAll}
                  className="w-5 h-5 text-primary bg-white border-gray-300 rounded focus:ring-primary"
                />
              </th>
              <th className="px-3 py-2.5 text-left text-sm font-medium text-[#5c6670]">
                {t('admin.users.table.columns.user')}
              </th>
              <th className="px-3 py-2.5 text-left text-sm font-medium text-[#5c6670] hidden md:table-cell">
                {t('admin.users.table.columns.status')}
              </th>
              <th className="px-3 py-2.5 text-left text-sm font-medium text-[#5c6670] hidden lg:table-cell">
                {t('admin.users.table.columns.groups')}
              </th>
              <th className="px-3 py-2.5 text-left text-sm font-medium text-[#5c6670] hidden xl:table-cell">
                {t('admin.users.table.columns.last_connection')}
              </th>
              <th className="px-3 py-2.5 text-right rounded-r-md"></th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                  {t('admin.users.table.empty')}
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <UsersTableRow
                  key={user.id}
                  user={user}
                  isSelected={selectedUsers.has(user.id)}
                  onToggleSelect={toggleSelectUser}
                  isCurrentUserAdmin={isCurrentUserAdmin}
                  onMenuToggle={handleMenuToggle}
                  registerButtonRef={(userId, el) => (buttonRefs.current[userId] = el)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {openMenuId && (
        <UsersTableActionMenu
          userId={openMenuId}
          position={menuPosition}
          removeLabel={removeLabel}
          onAction={onUserAction}
          onClose={() => setOpenMenuId(null)}
        />
      )}
    </div>
  );
};
